import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

// Search icon component
const SearchIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

// X icon component for clearing search
const XIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

// Back arrow icon component
const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 19l-7-7m0 0l7-7m-7 7h18"
    />
  </svg>
);

interface User {
  _id: string;
  name: string;
  handle: string;
  picture: string;
  headline?: string;
  bio?: string;
}

interface Post {
  _id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  likeCount: number;
  commentCount: number;
  user: {
    name: string;
    handle: string;
    picture: string;
  };
  deleted?: boolean;
}

interface SearchResults {
  users: User[];
  posts: Post[];
  pagination: {
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    limit: number;
    userCount: number;
    postCount: number;
  };
}

interface SearchBarProps {
  // No props needed since we'll navigate directly to post URLs
}

const SearchBar: React.FC<SearchBarProps> = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showUsersList, setShowUsersList] = useState(false);
  const [userListPage, setUserListPage] = useState(1);
  const [userListResults, setUserListResults] = useState<User[]>([]);
  const [userListLoading, setUserListLoading] = useState(false);
  const [userListPagination, setUserListPagination] = useState<any>(null);
  
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check URL and populate search box if on search page
  useEffect(() => {
    if (location.pathname === '/search') {
      const searchParams = new URLSearchParams(location.search);
      const urlQuery = searchParams.get('q');
      if (urlQuery) {
        setQuery(urlQuery);
      }
    } else {
      // Clear search when not on search page
      setQuery('');
      setResults(null);
      setShowResults(false);
    }
  }, [location.pathname, location.search]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowResults(false);
        setShowUsersList(false);
      }
    };

    if (showResults || showUsersList) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showResults, showUsersList]);

  // Search function with debouncing
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults(null);
      setShowResults(false);
      return;
    }

    setLoading(true);
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/search?q=${encodeURIComponent(searchQuery)}&type=all`,
        {
          method: 'GET',
          headers: headers,
        }
      );

      const data = await response.json();

      if (data.status === 'success') {
        setResults(data.data);
        setShowResults(true);
      } else {
        console.error('Search failed:', data.message);
        setResults(null);
        setShowResults(false);
      }
    } catch (error) {
      console.error('Error searching:', error);
      setResults(null);
      setShowResults(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load more users for the user list view
  const loadMoreUsers = useCallback(async (page: number) => {
    if (!query.trim()) return;

    setUserListLoading(true);
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers: { [key: string]: string } = {
        'Content-Type': 'application/json',
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/search?q=${encodeURIComponent(query)}&type=users&page=${page}&limit=20`,
        {
          method: 'GET',
          headers: headers,
        }
      );

      const data = await response.json();

      if (data.status === 'success') {
        if (page === 1) {
          setUserListResults(data.data.users);
        } else {
          setUserListResults(prev => [...prev, ...data.data.users]);
        }
        setUserListPagination(data.data.pagination);
      } else {
        console.error('User search failed:', data.message);
      }
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setUserListLoading(false);
    }
  }, [query]);

  // Handle input change with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for search
    searchTimeout.current = setTimeout(() => {
      performSearch(value);
    }, 300); // 300ms debounce
  };

  // Handle showing more users
  const handleShowMoreUsers = () => {
    setShowUsersList(true);
    setShowResults(false);
    setUserListPage(1);
    loadMoreUsers(1);
  };

  // Handle going back to search results
  const handleBackToResults = () => {
    setShowUsersList(false);
    setShowResults(true);
    setUserListResults([]);
    setUserListPage(1);
  };

  // Handle load more users
  const handleLoadMoreUsers = () => {
    const nextPage = userListPage + 1;
    setUserListPage(nextPage);
    loadMoreUsers(nextPage);
  };

  // Handle post click
  const handlePostClick = (post: Post) => {
    const postUrl = `/@${post.user.handle}/post/${post._id}`;
    navigate(postUrl);
    setShowResults(false);
    setShowUsersList(false);
  };

  // Handle user click
  const handleUserClick = () => {
    setShowResults(false);
    setShowUsersList(false);
  };

  // Handle Enter and Escape key presses
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim()) {
      navigateToSearchPage();
    } else if (e.key === 'Escape') {
      handleClearSearch();
    }
  };

  // Handle search button click to navigate to search page
  const handleSearchButtonClick = () => {
    if (query.trim()) {
      navigateToSearchPage();
    }
  };

  // Navigate to search page
  const navigateToSearchPage = () => {
    // Clear any pending search timeouts to prevent dropdown from appearing after navigation
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
      searchTimeout.current = null;
    }
    
    setShowResults(false);
    setShowUsersList(false);
    setResults(null); // Clear results to prevent dropdown from showing
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  // Handle clearing the search
  const handleClearSearch = () => {
    setQuery('');
    setResults(null);
    setShowResults(false);
    setShowUsersList(false);
    
    // Clear any pending search timeouts
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
      searchTimeout.current = null;
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  return (
    <div className="relative flex-1 max-w-md mx-4" ref={searchRef}>
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="w-full pl-3 pr-20 py-2 bg-custom-blue-700 border border-custom-blue-600 rounded-md text-white placeholder-custom-blue-300 focus:outline-none focus:ring-2 focus:ring-custom-blue-400 focus:border-custom-blue-400"
        />
        {/* Clear button - only show when there's text */}
        {query.trim() && (
          <button
            onClick={handleClearSearch}
            className="absolute right-10 top-1/2 transform -translate-y-1/2 h-6 w-6 text-custom-blue-300 hover:text-white transition-colors flex items-center justify-center"
            title="Clear search"
          >
            <XIcon className="h-4 w-4" />
          </button>
        )}
        {/* Search button or loading spinner */}
        {loading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue-300"></div>
          </div>
        ) : (
          <button
            onClick={handleSearchButtonClick}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 h-6 w-6 text-custom-blue-300 hover:text-white transition-colors flex items-center justify-center"
            title="Search (Press Enter)"
          >
            <SearchIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && results && !showUsersList && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
          {/* Users Section */}
          {results.users.length > 0 && (
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Users</h3>
              {results.users.slice(0, 3).map((user) => (
                <Link
                  key={user._id}
                  to={`/@${user.handle}`}
                  onClick={handleUserClick}
                  className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-md transition-colors"
                >
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-500">@{user.handle}</p>
                    {user.headline && (
                      <p className="text-xs text-gray-600 truncate">{user.headline}</p>
                    )}
                  </div>
                </Link>
              ))}
              {results.pagination.userCount > 3 && (
                <button
                  onClick={handleShowMoreUsers}
                  className="w-full mt-2 px-3 py-1 text-sm text-custom-blue-600 hover:text-custom-blue-800 hover:bg-gray-50 rounded transition-colors"
                >
                  Show more users ({results.pagination.userCount - 3} more)
                </button>
              )}
            </div>
          )}

          {/* Posts Section */}
          {results.posts.length > 0 && (
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Posts</h3>
              {results.posts.map((post) => (
                <div
                  key={post._id}
                  onClick={() => handlePostClick(post)}
                  className="p-3 hover:bg-gray-50 rounded-md cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start space-x-3">
                    <img
                      src={post.user.picture}
                      alt={post.user.name}
                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium text-gray-900">{post.user.name}</span>
                        <span className="text-xs text-gray-500">@{post.user.handle}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(post.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2">{post.content}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>{post.likeCount} likes</span>
                        <span>{post.commentCount} comments</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {results.users.length === 0 && results.posts.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              <p>No results found for "{query}"</p>
            </div>
          )}

          {/* View All Results Footer */}
          {(results.users.length > 0 || results.posts.length > 0) && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={navigateToSearchPage}
                className="w-full text-center text-sm text-custom-blue-600 hover:text-custom-blue-800 transition-colors font-medium"
              >
                View all results for "{query}"
              </button>
            </div>
          )}
        </div>
      )}

      {/* User List View */}
      {showUsersList && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center mb-4">
              <button
                onClick={handleBackToResults}
                className="flex items-center space-x-2 text-custom-blue-600 hover:text-custom-blue-800 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="text-sm">Back to results</span>
              </button>
            </div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              All Users ({userListPagination?.userCount || 0})
            </h3>
            {userListResults.map((user) => (
              <Link
                key={user._id}
                to={`/@${user.handle}`}
                onClick={handleUserClick}
                className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-md transition-colors"
              >
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500">@{user.handle}</p>
                  {user.headline && (
                    <p className="text-xs text-gray-600 truncate">{user.headline}</p>
                  )}
                </div>
              </Link>
            ))}
            {userListPagination?.hasNextPage && (
              <button
                onClick={handleLoadMoreUsers}
                disabled={userListLoading}
                className="w-full mt-2 px-3 py-2 text-sm text-custom-blue-600 hover:text-custom-blue-800 hover:bg-gray-50 rounded transition-colors disabled:opacity-50"
              >
                {userListLoading ? 'Loading...' : 'Load more users'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
